import React from "react";
import { toast } from "react-toastify";
import ReceivedGiftCards from "../../Modules/ReceivedGiftCards/ReceivedGiftCards";
import { GiftCardPreview, previewGiftCardApi } from "../../Service/giftCard";
import "./ApplyGiftCard.scss";

interface ApplyGiftCardProps {
  checkoutType: "service" | "course" | "event" | "membership";
  product: Record<string, unknown>;
  selectedCode: string;
  onApplied: (code: string, preview: GiftCardPreview | null) => void;
}

const ApplyGiftCard = ({
  checkoutType,
  product,
  selectedCode,
  onApplied,
}: ApplyGiftCardProps) => {
  const [manualCode, setManualCode] = React.useState("");
  const [preview, setPreview] = React.useState<GiftCardPreview | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);

  const applyCode = async (code: string) => {
    if (!code) {
      return;
    }

    setIsLoading(true);
    try {
      const response: any = await previewGiftCardApi({
        gift_card_code: code,
        checkout_type: checkoutType,
        qty: 1,
        ...product,
      });

      if (response?.data?.success && response?.data?.data) {
        const nextPreview = response.data.data as GiftCardPreview;
        setPreview(nextPreview);
        onApplied(code, nextPreview);
        toast.success("Gift card applied");
      } else {
        const message = response?.data?.message;
        toast.error(Array.isArray(message) ? message.join(", ") : "Unable to apply gift card");
      }
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error(Array.isArray(message) ? message.join(", ") : err?.message || "Unable to apply gift card");
    } finally {
      setIsLoading(false);
    }
  };

  const clearGiftCard = () => {
    setPreview(null);
    setManualCode("");
    onApplied("", null);
  };

  return (
    <div className="ApplyGiftCard">
      <div className="ApplyGiftCard__title">Apply a gift card</div>

      <ReceivedGiftCards
        apply={applyCode}
        coupon={selectedCode}
        hideEmptyState
        product={{}}
        variant="checkout"
      />

      <div className="ApplyGiftCard__manual">
        <input
          type="text"
          className="form-control ApplyGiftCard__manualInput"
          placeholder="Enter gift card code"
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && manualCode.trim()) {
              applyCode(manualCode.trim());
            }
          }}
        />
        <button
          type="button"
          className="btn secondary-teal-btn ApplyGiftCard__manualBtn"
          disabled={isLoading || !manualCode.trim()}
          onClick={() => applyCode(manualCode.trim())}
        >
          Apply
        </button>
      </div>

      {preview && (
        <div className="ApplyGiftCard__breakdown">
          <div className="ApplyGiftCard__line">
            <span>Subtotal</span>
            <span>${preview.subtotal_amount.toFixed(2)}</span>
          </div>
          <div className="ApplyGiftCard__line">
            <span>Gift card</span>
            <span>- ${preview.discount_amount.toFixed(2)}</span>
          </div>
          <div className="ApplyGiftCard__line ApplyGiftCard__line--total">
            <span>To pay</span>
            <span>${preview.pay_amount.toFixed(2)}</span>
          </div>
          <div className="ApplyGiftCard__remaining">
            Remaining on card after purchase: ${preview.remaining_after_amount.toFixed(2)}
          </div>
          <button type="button" className="ApplyGiftCard__removeBtn" onClick={clearGiftCard}>
            Remove gift card
          </button>
        </div>
      )}
    </div>
  );
};

export default ApplyGiftCard;
